import ConfigurationOptions from '../../../dashboard-components/layouts/ConfigurationOptions.jsx';
import React, { useMemo, useState } from 'react';
import { Edit } from 'lucide-react';
import { FaTimes } from 'react-icons/fa';
import ModernTimeDropdown from '../../../dashboard-components/layouts/modernDropdown';
import server from '../../../api/server.js';

const ANALYTICS_TYPES = [
  'Yes/No/NA',
  'Categorical - Single Value',
  'Categorical - Multiple Values',
  'Number',
  'Text',
  'Date and Time (ISO Format - YYYY-MM-DDTHH:MM:SS)'
];

const toVariableName = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[\s\-]+/g, '_')
  .replace(/[^a-z0-9_]/g, '')
  .replace(/_+/g, '_')
  .replace(/^_+|_+$/g, '');

const getArray = (value) => (Array.isArray(value) ? value : []);

const resolveOptionsForType = (type, options) => {
  if (type === 'Yes/No/NA') return ['Yes', 'No', 'NA'];
  if (type === 'Number' || type === 'Text' || type === 'Date and Time') return [];
  return getArray(options);
};

const normalizeStatus = (value, fallback = 'active') => {
  const normalized = String(value || fallback).trim().toLowerCase();
  return normalized === 'inactive' ? 'inactive' : 'active';
};

const isSystemParameter = (item) => (
  item?.system === 1 || item?.system === true || item?.system === '1'
);

const normalizeOutputTypeForBackend = (type) => {
  if (type === 'Categorical - Single Value') return 'Categorical';
  if (typeof type === 'string' && type.startsWith('Date and Time')) return 'Date and Time';
  return type || 'Yes/No/NA';
};

const normalizeOutputTypeForFrontend = (type) => {
  if (type === 'Categorical') return 'Categorical - Single Value';
  if (typeof type === 'string' && type.startsWith('Date and Time')) return 'Date and Time - (ISO Format - YYYY-MM-DDTHH:MM:SS)';
  return type || 'Number';
};

const isLogicSubparameter = (item) => item?.subparameter_type === 'logic_based';

const validateLogicDependencies = (items) => {
  const active = items.filter((item) => normalizeStatus(item.status) === 'active');
  const byName = new Map(active.map((item) => [toVariableName(item.display_name), item]));
  const orders = new Set();
  for (const item of active.filter(isLogicSubparameter)) {
    const order = Number(item.execution_order);
    if (!Number.isInteger(order) || order < 1 || orders.has(order)) {
      return 'Logic execution orders must be unique positive integers.';
    }
    orders.add(order);
    for (const name of getArray(item.subparameters_used)) {
      const source = byName.get(name);
      if (!source || source === item || (isLogicSubparameter(source) && Number(source.execution_order) >= order)) {
        return `${item.display_name}: select active extracted values or earlier logic subparameters only.`;
      }
    }
  }
  return '';
};

const parseLogicDefault = (type, raw, options) => {
  if (type === 'Number') {
    if (!String(raw).trim() || !Number.isFinite(Number(raw))) throw new Error('Default Value must be a finite number.');
    return Number(raw);
  }
  if (type === 'Categorical - Multiple Values') {
    let values;
    try { values = JSON.parse(raw); } catch { throw new Error('Enter Default Value as a JSON array, for example ["Yes"].'); }
    if (!Array.isArray(values) || values.some((value) => !options.includes(value))) throw new Error('Default Value must contain only configured possible values.');
    return values;
  }
  if ((type === 'Yes/No/NA' || type === 'Categorical - Single Value') && !options.includes(raw)) throw new Error('Default Value must match a possible value.');
  if (String(type).startsWith('Date and Time') && (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(raw) || !Number.isFinite(Date.parse(raw)))) throw new Error('Default Value must be an ISO date and time.');
  return raw;
};

const InbuiltAnalyticsConfig = ({
  value,
  onChange,
  showToast,
  clientId,
  serviceId,
  reservedNames = [],
}) => {
  const [activeTab, setActiveTab] = useState('subparameters');
  const [subparamModalOpen, setSubparamModalOpen] = useState(false);
  const [subparamEditIndex, setSubparamEditIndex] = useState(null);
  const [subparamForm, setSubparamForm] = useState({
    display_name: '',
    instruction: '',
      context_scope: 'conversation',
    output_type: 'Yes/No/NA',
    possible_values: [],
    status: 'active',
    system: 0, subparameter_type: 'llm_based', logic: '', execution_order: 1, subparameters_used: [], use_default: false, default_text: ''
  });
  const [subparamNewOption, setSubparamNewOption] = useState('');

  const [paramModalOpen, setParamModalOpen] = useState(false);
  const [paramEditIndex, setParamEditIndex] = useState(null);
  const [paramForm, setParamForm] = useState({
    display_name: '',
    logic: '',
    subparameters_used: [],
    output_type: 'Number',
    possible_values: [],
    status: 'active',
    fatal: 0,
    weightage: 0,
    threshold_score: 0,
    logic_explanation: ''
  });
  const [selectedSubparameter, setSelectedSubparameter] = useState('');
  const [queryError, setQueryError] = useState('');
  const [logicLoading, setLogicLoading] = useState(false);

  const isReservedName = (name) => {
    const key = toVariableName(name);
    return key.startsWith('prev_') || reservedNames.includes(key);
  };
  const subparameters = getArray(value?.subparameters);
  const parameters = getArray(value?.parameters);
  const previousParameterVariables = useMemo(
    () => Array.from(new Set(
      parameters
        .map((parameter) => `prev_${toVariableName(parameter?.display_name)}`)
        .filter((name) => name !== 'prev_')
    )),
    [parameters]
  );

  const availableSubparameterVariables = useMemo(
    () => Array.from(new Set([
      ...subparameters
        .filter((subparam) => normalizeStatus(subparam?.status, 'active') !== 'inactive')
        .map((subparam) => toVariableName(subparam?.display_name))
        .filter(Boolean),
      ...previousParameterVariables
    ])),
    [subparameters, previousParameterVariables]
  );

  const updateConfig = (nextPartial) => {
    const nextParameters = getArray(nextPartial?.parameters ?? parameters);
    const previousNames = new Set(nextParameters.map((parameter) => `prev_${toVariableName(parameter?.display_name)}`));
    const nextSubparameters = getArray(nextPartial?.subparameters ?? subparameters)
      .filter((item) => !(isSystemParameter(item) && toVariableName(item?.display_name).startsWith('prev_'))
        || previousNames.has(toVariableName(item?.display_name)));
    const existingSubparameterNames = new Set(
      nextSubparameters
        .map((subparam) => toVariableName(subparam?.display_name))
        .filter(Boolean)
    );
    const missingPreviousSubparameters = nextParameters
      .map((parameter) => {
        const parameterName = toVariableName(parameter?.display_name);
        if (!parameterName) return null;
        const display_name = `prev_${parameterName}`;
        if (existingSubparameterNames.has(display_name)) return null;
        return {
          display_name,
          instruction: 'Value of this parameter from the parent conversation, if one exists.',
          output_type: 'Text',
          possible_values: [],
          system: 1
        };
      })
      .filter(Boolean);
    const nextValue = {
      ...(value || {}),
      ...(nextPartial || {}),
      subparameters: [...nextSubparameters, ...missingPreviousSubparameters],
      parameters: nextParameters
    };
    onChange?.(nextValue);
  };

  const openAddSubparamModal = () => {
    setSubparamEditIndex(null);
    setSubparamForm({
      display_name: '',
      instruction: '',
      context_scope: 'conversation',
      output_type: 'Yes/No/NA',
      possible_values: [],
      status: 'active',
      system: 0, subparameter_type: 'llm_based', logic: '', execution_order: Math.max(0, ...subparameters.filter(isLogicSubparameter).map((item) => Number(item.execution_order) || 0)) + 1, subparameters_used: [], use_default: false, default_text: ''
    });
    setSubparamNewOption('');
    setSubparamModalOpen(true);
  };

  const openEditSubparamModal = (index) => {
    const selected = subparameters[index];
    if (!selected || isSystemParameter(selected)) return;
    setSubparamEditIndex(index);
    setSubparamForm({
      display_name: selected.display_name || '',
      instruction: selected.instruction || '',
      context_scope: selected.context_scope || 'conversation',
      output_type: selected.output_type || 'Yes/No/NA',
      possible_values: getArray(selected.possible_values),
      status: normalizeStatus(selected.status, 'active'),
      system: selected.system ? 1 : 0,
      subparameter_type: selected.subparameter_type || 'llm_based',
      logic: selected.logic || '', execution_order: selected.execution_order || 1,
      subparameters_used: getArray(selected.subparameters_used),
      use_default: Object.prototype.hasOwnProperty.call(selected, 'default_value'),
      default_text: Array.isArray(selected.default_value) ? JSON.stringify(selected.default_value) : String(selected.default_value ?? '')
    });
    setSubparamNewOption('');
    setSubparamModalOpen(true);
  };

  const saveSubparameter = () => {
    const display_name = String(subparamForm.display_name || '').trim();
    const output_type = subparamForm.output_type || 'Yes/No/NA';
    if (!display_name) {
      showToast?.('Subparameter name is required', 'error');
      return;
    }
    if (isReservedName(display_name)) {
      showToast?.('This name is reserved for system analytics. Use a different name.', 'error');
      return;
    }
    const duplicate = subparameters.some((item, idx) => idx !== subparamEditIndex && toVariableName(item?.display_name) === toVariableName(display_name));
    if (duplicate) {
      showToast?.('Subparameter name already exists', 'error');
      return;
    }
    const possible_values = resolveOptionsForType(output_type, subparamForm.possible_values);
    if ((output_type === 'Categorical - Single Value' || output_type === 'Categorical - Multiple Values') && possible_values.length === 0) {
      showToast?.('Add at least one possible value', 'error');
      return;
    }
    const nextSubparam = {
      display_name,
      ...(isLogicSubparameter(subparamForm) ? {
        subparameter_type: 'logic_based', logic: String(subparamForm.logic || '').trim(),
        execution_order: Number(subparamForm.execution_order),
        subparameters_used: [...getArray(subparamForm.subparameters_used)],
      } : {instruction: String(subparamForm.instruction || '').trim(), context_scope: subparamForm.context_scope || 'conversation'}),
      output_type,
      possible_values,
      status: normalizeStatus(subparamForm.status, 'active'),
      system: 0
    };
    if (isLogicSubparameter(subparamForm)) {
      if (!nextSubparam.logic) { showToast?.('Python logic is required', 'error'); return; }
      if (subparamForm.use_default) {
        try { nextSubparam.default_value = parseLogicDefault(output_type, subparamForm.default_text, possible_values); }
        catch (error) { showToast?.(error.message, 'error'); return; }
      }
    }
    const nextSubparameters = [...subparameters];
    if (subparamEditIndex == null) {
      nextSubparameters.push(nextSubparam);
    } else {
      nextSubparameters[subparamEditIndex] = nextSubparam;
    }
    const dependencyError = validateLogicDependencies(nextSubparameters);
    if (dependencyError) { showToast?.(dependencyError, 'error'); return; }
    updateConfig({ subparameters: nextSubparameters });
    setSubparamModalOpen(false);
    setSubparamEditIndex(null);
  };

  const addSubparameterOption = () => {
    const optionValue = String(subparamNewOption || '').trim();
    if (!optionValue) return;
    if (getArray(subparamForm.possible_values).includes(optionValue)) return;
    setSubparamForm((prev) => ({ ...prev, possible_values: [...getArray(prev.possible_values), optionValue] }));
    setSubparamNewOption('');
  };

  const removeSubparameterOption = (idx) => {
    setSubparamForm((prev) => ({
      ...prev,
      possible_values: getArray(prev.possible_values).filter((_, index) => index !== idx)
    }));
  };

  const updateSubparameterStatus = (idx, checked) => {
    const nextSubparameters = [...subparameters];
    if (!nextSubparameters[idx]) return;
    if (isSystemParameter(nextSubparameters[idx])) return;
    nextSubparameters[idx] = { ...nextSubparameters[idx], status: checked ? 'active' : 'inactive' };
    const dependencyError = validateLogicDependencies(nextSubparameters);
    if (dependencyError) { showToast?.(dependencyError, 'error'); return; }
    updateConfig({ subparameters: nextSubparameters });
  };

  const openAddParameterModal = () => {
    setParamEditIndex(null);
    setQueryError('');
    setParamForm({
      display_name: '',
      logic: '',
      subparameters_used: [],
      output_type: 'Number',
      possible_values: [],
      status: 'active',
      fatal: 0,
      weightage: 0,
      threshold_score: 0,
      logic_explanation: ''
    });
    setSelectedSubparameter('');
    setParamModalOpen(true);
  };

  const openEditParameterModal = (index) => {
    const selected = parameters[index];
    if (!selected) return;
    setParamEditIndex(index);
    setQueryError('');
    setParamForm({
      display_name: selected.display_name || '',
      logic: selected.logic || '',
      subparameters_used: getArray(selected.subparameters_used),
      output_type: normalizeOutputTypeForFrontend(selected.output_type) || 'Number',
      possible_values: getArray(selected.possible_values),
      status: normalizeStatus(selected.status, 'active'),
      fatal: Number.isFinite(Number(selected?.fatal)) ? Number(selected.fatal) : 0,
      weightage: Number.isFinite(Number(selected?.weightage)) ? Number(selected.weightage) : 0,
      threshold_score: Number.isFinite(Number(selected?.threshold_score)) ? Number(selected.threshold_score) : 0,
      logic_explanation: selected.logic_explanation || ''
    });
    setSelectedSubparameter('');
    setParamModalOpen(true);
  };

  const addSubparameterToParameter = () => {
    const nextVariable = String(selectedSubparameter || '').trim();
    if (!nextVariable) return;
    if (!availableSubparameterVariables.includes(nextVariable)) {
      showToast?.('Select a valid subparameter', 'error');
      return;
    }
    if (paramForm.subparameters_used.includes(nextVariable)) return;
    setParamForm((prev) => ({ ...prev, subparameters_used: [...prev.subparameters_used, nextVariable] }));
    setSelectedSubparameter('');
  };

  const removeSubparameterFromParameter = (variableName) => {
    setParamForm((prev) => ({
      ...prev,
      subparameters_used: prev.subparameters_used.filter((item) => item !== variableName)
    }));
  };

  const verifyParameterLogic = async () => {
    const logic = String(paramForm.logic || '').trim();
    if (!logic) {
      setQueryError('Logic cannot be empty');
      return false;
    }
    try {
      setLogicLoading(true);
      const expectedReturn = paramForm.output_type === 'Number' ? 'numeric' : 'string';
      const matchType = paramForm.output_type === 'Number' ? 'exact' : 'subset';
      const verificationSubparameters = subparameters.map((subparam) => ({
        ...subparam,
        display_name: toVariableName(subparam.display_name),
        output_type: normalizeOutputTypeForBackend(subparam?.output_type),
        status: normalizeStatus(subparam?.status, 'active')
      }));
      const verificationNames = new Set(verificationSubparameters.map((subparam) => subparam.display_name));
      previousParameterVariables.forEach((name) => {
        if (!verificationNames.has(name)) {
          verificationSubparameters.push({
            display_name: name,
            instruction: 'Value of this parameter from the parent conversation, if one exists.',
            output_type: 'Text',
            possible_values: [],
            status: 'active',
            system: 1
          });
        }
      });
      const result = await server.verifyQuery(
        clientId,
        serviceId,
        logic,
        paramForm.subparameters_used,
        verificationSubparameters,
        expectedReturn,
        matchType
      );
      if (result.status !== 200) {
        setQueryError('Unable to verify logic right now');
        return false;
      }
      const response = await result.json();
      const firstError = Array.isArray(response?.errors) && response.errors.length > 0 ? response.errors[0] : null;
      if (firstError) {
        setQueryError(`${firstError.error_type}: ${firstError.error_description}`);
        return false;
      }
      setQueryError('');
      setParamForm((prev) => ({
        ...prev,
        logic: response?.formatted || prev.logic,
        logic_explanation: response?.logic_explanation || prev.logic_explanation
      }));
      showToast?.('Logic validated successfully', 'success');
      return true;
    } catch (error) {
      setQueryError('Error validating logic');
      return false;
    } finally {
      setLogicLoading(false);
    }
  };

  const saveParameter = async () => {
    const display_name = String(paramForm.display_name || '').trim();
    const logic = String(paramForm.logic || '').trim();
    if (!display_name) {
      showToast?.('Parameter name is required', 'error');
      return;
    }
    if (isReservedName(display_name)) {
      showToast?.('This name is reserved for system analytics. Use a different name.', 'error');
      return;
    }
    if (!logic) {
      showToast?.('Logic is required', 'error');
      return;
    }
    if (paramForm.subparameters_used.length === 0) {
      showToast?.('Select at least one subparameter', 'error');
      return;
    }
    const duplicate = parameters.some((item, idx) => idx !== paramEditIndex && String(item?.display_name || '').trim().toLowerCase() === display_name.toLowerCase());
    if (duplicate) {
      showToast?.('Parameter name already exists', 'error');
      return;
    }
    const parameterOptions = [...new Set(getArray(paramForm.possible_values).map((value) => String(value).trim()).filter(Boolean))];
    const isValid = await verifyParameterLogic();
    if (!isValid) {
      showToast?.('Please fix logic validation errors', 'error');
      return;
    }
    const nextParameter = {
      display_name,
      logic: String(paramForm.logic || '').trim(),
      subparameters_used: [...paramForm.subparameters_used],
      output_type: normalizeOutputTypeForBackend(paramForm.output_type) || 'Number',
      possible_values: resolveOptionsForType(normalizeOutputTypeForBackend(paramForm.output_type), parameterOptions),
      status: normalizeStatus(paramForm.status, 'active'),
      fatal: Number.isFinite(Number(paramForm.fatal)) ? Number(paramForm.fatal) : 0,
      weightage: Number.isFinite(Number(paramForm.weightage)) ? Number(paramForm.weightage) : 0,
      threshold_score: Number.isFinite(Number(paramForm.threshold_score)) ? Number(paramForm.threshold_score) : 0,
      logic_explanation: paramForm.logic_explanation || ''
    };
    const nextParameters = [...parameters];
    if (paramEditIndex == null) {
      nextParameters.push(nextParameter);
    } else {
      nextParameters[paramEditIndex] = nextParameter;
    }
    updateConfig({ parameters: nextParameters });
    setParamModalOpen(false);
    setParamEditIndex(null);
  };

  const updateParameterStatus = (idx, checked) => {
    const nextParameters = [...parameters];
    if (!nextParameters[idx]) return;
    nextParameters[idx] = { ...nextParameters[idx], status: checked ? 'active' : 'inactive' };
    updateConfig({ parameters: nextParameters });
  };

  return (
    <div>
      <div className="border border-gray-200 rounded-lg p-4 mb-5 bg-white">
        <h3 className="text-sm font-medium text-gray-900">Run inbuilt analytics for</h3>
        <p className="mt-1 text-xs text-gray-500">Enable these only when analytics should also be calculated for conversations that are not connected or are non-meaningful. Existing clients remain unchanged by default.</p>
        <div className="mt-3 flex flex-col sm:flex-row gap-3 sm:gap-6">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={value?.run_for_not_connected === true}
              onChange={(event) => updateConfig({ run_for_not_connected: event.target.checked })}
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
            />
            Not connected calls
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={value?.run_for_non_meaningful === true}
              onChange={(event) => updateConfig({ run_for_non_meaningful: event.target.checked })}
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
            />
            Non-meaningful calls
          </label>
        </div>
      </div>
      <div className="border-b border-gray-200">
        <nav className="flex" aria-label="Inbuilt analytics tabs">
          <button
            onClick={() => setActiveTab('subparameters')}
            className={`py-3 px-5 text-center border-b-2 font-medium text-sm ${
              activeTab === 'subparameters'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Subparameters
          </button>
          <button
            onClick={() => setActiveTab('parameters')}
            className={`py-3 px-5 text-center border-b-2 font-medium text-sm ${
              activeTab === 'parameters'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Parameters
          </button>
        </nav>
      </div>

      {activeTab === 'subparameters' && (
        <div className="pt-5">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-lg font-medium text-gray-900">Inbuilt Analytics - Subparameters</h3>
            <button type="button" onClick={openAddSubparamModal} className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-md text-sm">
              Add Subparameter
            </button>
          </div>

          <div className="overflow-x-auto mt-4">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 font-medium text-gray-700 text-left">Name</th>
                  <th className="px-4 py-2 font-medium text-gray-700 text-left">Kind</th>
                  <th className="px-4 py-2 font-medium text-gray-700 text-left">Definition</th>
                  <th className="px-4 py-2 font-medium text-gray-700 text-left">Type</th>
                  <th className="px-4 py-2 font-medium text-gray-700 text-left">Options</th>
                  <th className="px-4 py-2 font-medium text-gray-700 text-left">Status</th>
                  <th className="px-4 py-2 font-medium text-gray-700 text-left">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {subparameters.length === 0 ? (
                  <tr>
                    <td className="px-4 py-4 text-gray-500 text-center" colSpan="7">No subparameters added yet.</td>
                  </tr>
                ) : (
                  subparameters.map((item, idx) => (
                    <tr key={`${item.display_name || 'subparameter'}-${idx}`} className="align-top">
                      <td className="px-4 py-2 text-gray-900">{item?.display_name || '-'}</td>
                      <td className="px-4 py-2 text-gray-700 whitespace-nowrap">
                        {isSystemParameter(item) ? 'System' : isLogicSubparameter(item) ? 'Logic' : 'LLM'}
                        {isLogicSubparameter(item) && <div className="text-xs text-indigo-600 mt-1">Order {item.execution_order}</div>}
                      </td>
                      <td className="px-4 py-2 text-gray-700 max-w-xs whitespace-pre-wrap">{String(isLogicSubparameter(item) ? item.logic : item.instruction || '-').slice(0, 100)}</td>
                      <td className="px-4 py-2 text-gray-700">{item?.output_type || '-'}</td>
                      <td className="px-4 py-2 text-gray-700 max-w-xs whitespace-pre-wrap"><ConfigurationOptions options={item?.possible_values} /></td>
                      <td className="px-4 py-2">
                        <div className="relative inline-block w-12 align-middle select-none">
                          <input
                            type="checkbox"
                            checked={normalizeStatus(item?.status, 'active') === 'active'}
                            onChange={(e) => updateSubparameterStatus(idx, e.target.checked)}
                            className="sr-only"
                            id={`toggle-subparameter-status-${idx}`}
                            disabled={isSystemParameter(item)}
                          />
                          <label
                            htmlFor={`toggle-subparameter-status-${idx}`}
                            aria-disabled={isSystemParameter(item)}
                            onClick={(event) => {
                              if (isSystemParameter(item)) event.preventDefault();
                            }}
                            className={`block h-5 rounded-full transition-colors duration-200 ease-in-out ${isSystemParameter(item) ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} ${(normalizeStatus(item?.status, 'active') === 'active') ? 'bg-indigo-600' : 'bg-gray-200'}`}
                          >
                            <span className={`block h-5 w-5 bg-white rounded-full shadow transform duration-200 ease-in-out ${(normalizeStatus(item?.status, 'active') === 'active') ? 'translate-x-7' : 'translate-x-0'}`} />
                          </label>
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        {isSystemParameter(item) ? (
                          <span className="text-gray-400 text-sm">System Parameter</span>
                        ) : (
                          <button type="button" onClick={() => openEditSubparamModal(idx)} className="text-indigo-600 hover:text-indigo-800 text-sm">
                            <Edit className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'parameters' && (
        <div className="pt-5">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-lg font-medium text-gray-900">Inbuilt Analytics - Parameters</h3>
            <button type="button" onClick={openAddParameterModal} className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-md text-sm">
              Add Parameter
            </button>
          </div>

          <div className="overflow-x-auto mt-4">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 font-medium text-gray-700 text-left">Name</th>
                  <th className="px-4 py-2 font-medium text-gray-700 text-left">Subparameters Used</th>
                  <th className="px-4 py-2 font-medium text-gray-700 text-left">Logic</th>
                  <th className="px-4 py-2 font-medium text-gray-700 text-left">Output Type</th>
                  <th className="px-4 py-2 font-medium text-gray-700 text-left">Status</th>
                  <th className="px-4 py-2 font-medium text-gray-700 text-left">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {parameters.length === 0 ? (
                  <tr>
                    <td className="px-4 py-4 text-gray-500 text-center" colSpan="6">No parameters added yet.</td>
                  </tr>
                ) : (
                  parameters.map((item, idx) => (
                    <tr key={`${item.display_name || 'parameter'}-${idx}`} className="align-top">
                      <td className="px-4 py-2 text-gray-900">{item?.display_name || '-'}</td>
                      <td className="px-4 py-2 text-gray-700 max-w-xs whitespace-pre-wrap">{getArray(item?.subparameters_used).length > 0 ? item.subparameters_used.length > 100 ? item.subparameters_used.slice(0, 100) + '...' : item.subparameters_used.join(', ') : '-'}</td>
                      <td className="px-4 py-2 text-gray-700 max-w-xs whitespace-pre-wrap">{item?.logic || '-'}</td>
                      <td className="px-4 py-2 text-gray-700">{item?.output_type || '-'}</td>
                      <td className="px-4 py-2">
                        <div className="relative inline-block w-12 align-middle select-none">
                          <input
                            type="checkbox"
                            checked={normalizeStatus(item?.status, 'active') === 'active'}
                            onChange={(e) => updateParameterStatus(idx, e.target.checked)}
                            className="sr-only"
                            id={`toggle-parameter-status-${idx}`}
                          />
                          <label
                            htmlFor={`toggle-parameter-status-${idx}`}
                            className={`block h-5 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${(normalizeStatus(item?.status, 'active') === 'active') ? 'bg-indigo-600' : 'bg-gray-200'}`}
                          >
                            <span className={`block h-5 w-5 bg-white rounded-full shadow transform duration-200 ease-in-out ${(normalizeStatus(item?.status, 'active') === 'active') ? 'translate-x-7' : 'translate-x-0'}`} />
                          </label>
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <button type="button" onClick={() => openEditParameterModal(idx)} className="text-indigo-600 hover:text-indigo-800 text-sm">
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {subparamModalOpen && (
        <div style={{ zIndex: '9999999999', position: 'fixed', top: 0, left: 0, height: '100%', width: '100%', background: 'rgba(0,0,0, 0.5)' }}>
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl relative max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center p-4 pb-2">
                <h2 className="text-lg text-gray-800" style={{ fontWeight: '550' }}>{subparamEditIndex != null ? 'Edit Subparameter' : 'Add Subparameter'}</h2>
                <button onClick={() => setSubparamModalOpen(false)} className="text-gray-500 hover:text-gray-700"><FaTimes style={{ color: 'slategray' }} /></button>
              </div>
              <div className="p-6 pt-2 text-sm text-gray-600">
                <div className="grid gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                    <input
                      type="text"
                      value={subparamForm.display_name}
                      onChange={(e) => setSubparamForm((prev) => ({ ...prev, display_name: e.target.value }))}
                    disabled={subparamEditIndex != null && !isReservedName(subparameters[subparamEditIndex]?.display_name)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500 ${subparamEditIndex != null ? 'bg-gray-50 text-gray-600 cursor-not-allowed' : ''}`}
                      placeholder="Enter subparameter name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Processing type</label>
                    <ModernTimeDropdown width="w-full" ariaLabel="Processing type" value={isLogicSubparameter(subparamForm) ? 'Logic Based' : 'LLM Based'} options={['LLM Based', 'Logic Based']} onChange={(e) => setSubparamForm((prev) => ({ ...prev, subparameter_type: e.target.value === 'Logic Based' ? 'logic_based' : 'llm_based' }))} />
                  </div>
                  {!isLogicSubparameter(subparamForm) && <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Context
                    </label>
                    <ModernTimeDropdown
                      width="w-full"
                      ariaLabel="Context"
                      value={subparamForm.context_scope === 'customer' ? 'Customer context' : 'Conversation context (default)'}
                      options={['Conversation context (default)', 'Customer context']}
                      onChange={(e) => setSubparamForm((prev) => ({ ...prev, context_scope: e.target.value === 'Customer context' ? 'customer' : 'conversation' }))}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {subparamForm.context_scope === 'customer'
                        ? 'Uses up to 50 recent conversations, including this call and non-connected calls.'
                        : 'Uses this conversation only.'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Definition</label>
                    <textarea
                      value={subparamForm.instruction}
                      onChange={(e) => setSubparamForm((prev) => ({ ...prev, instruction: e.target.value }))}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500"
                      placeholder="Describe what this subparameter means"
                    />
                  </div>
                  </>}
                  {isLogicSubparameter(subparamForm) && <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="analytics-logic-order">Execution order</label>
                      <input id="analytics-logic-order" type="number" min="1" step="1" value={subparamForm.execution_order} onChange={(e) => setSubparamForm((prev) => ({ ...prev, execution_order: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                      <p className="mt-1 text-xs text-gray-500">Extracted values run first. Logic runs in ascending order, before analytics parameters. Orders must be unique.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Inputs</label>
                      <div className="max-h-36 overflow-y-auto border border-gray-200 rounded-lg p-2">
                        {subparameters.filter((item, idx) => idx !== subparamEditIndex && normalizeStatus(item.status) === 'active' && (!isLogicSubparameter(item) || Number(item.execution_order) < Number(subparamForm.execution_order))).map((item) => {
                          const name = toVariableName(item.display_name);
                          return <label key={name} className="flex gap-2 items-center py-1"><input type="checkbox" checked={getArray(subparamForm.subparameters_used).includes(name)} onChange={(e) => setSubparamForm((prev) => ({ ...prev, subparameters_used: e.target.checked ? [...prev.subparameters_used, name] : prev.subparameters_used.filter((value) => value !== name) }))} />{name}</label>;
                        })}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">{getArray(subparamForm.subparameters_used).map((name) => <button type="button" key={name} onClick={() => setSubparamForm((prev) => ({ ...prev, subparameters_used: prev.subparameters_used.filter((value) => value !== name) }))} className="px-2 py-1 bg-gray-100 rounded text-xs" aria-label={`Remove input ${name}`}>{name} ×</button>)}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="analytics-subparameter-logic">Python logic</label>
                      <textarea id="analytics-subparameter-logic" rows={6} value={subparamForm.logic} onChange={(e) => setSubparamForm((prev) => ({ ...prev, logic: e.target.value }))} placeholder="return selected_input" className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono" />
                      <p className="mt-1 text-xs text-gray-500">Return the selected output type. Logic is validated when the agent configuration is saved.</p>
                    </div>
                    <div>
                      <label className="flex gap-2 items-center"><input type="checkbox" checked={subparamForm.use_default} onChange={(e) => setSubparamForm((prev) => ({ ...prev, use_default: e.target.checked }))} />Use Default Value</label>
                      {subparamForm.use_default && <input aria-label="Default Value" type="text" value={subparamForm.default_text} onChange={(e) => setSubparamForm((prev) => ({ ...prev, default_text: e.target.value }))} className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg" />}
                      <p className="mt-1 text-xs text-gray-500">Used when execution fails or the output is invalid. Match the output type; multiple values use a JSON array. An enabled empty text value is a valid default.</p>
                    </div>
                  </>}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                    <ModernTimeDropdown
                      value={subparamForm.output_type === 'Date and Time' ? 'Date and Time (ISO Format - YYYY-MM-DDTHH:MM:SS)' : subparamForm.output_type}
                      onChange={(e) => {
                        const raw = e?.target?.value;
                        const normalized = (typeof raw === 'string' && raw.startsWith('Date and Time')) ? 'Date and Time' : raw;
                        setSubparamForm((prev) => ({ ...prev, output_type: normalized }));
                      }}
                      options={ANALYTICS_TYPES}
                      width="w-full"
                    isDisabled={subparamEditIndex != null}
                    />
                  </div>
                  {(subparamForm.output_type === 'Categorical - Single Value' || subparamForm.output_type === 'Categorical - Multiple Values') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Possible Values</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={subparamNewOption}
                          onChange={(e) => setSubparamNewOption(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSubparameterOption(); } }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500"
                          placeholder="Add a value and click Add"
                        />
                        <button type="button" onClick={addSubparameterOption} className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm">
                          Add
                        </button>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {getArray(subparamForm.possible_values).map((option, idx) => (
                          <span key={`${option}-${idx}`} className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 px-2 py-1 rounded">
                            {option}
                            <button type="button" className="text-gray-500 hover:text-gray-700" onClick={() => removeSubparameterOption(idx)}>
                              <FaTimes style={{ fontSize: '10px' }} />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-3 p-6">
                <button onClick={() => setSubparamModalOpen(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm">
                  Cancel
                </button>
                <button onClick={saveSubparameter} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm">
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {paramModalOpen && (
        <div style={{ zIndex: '9999999999', position: 'fixed', top: 0, left: 0, height: '100%', width: '100%', background: 'rgba(0,0,0, 0.5)' }}>
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl relative">
              <div className="flex justify-between items-center p-4 pb-2">
                <h2 className="text-lg text-gray-800" style={{ fontWeight: '550' }}>{paramEditIndex != null ? 'Edit Parameter' : 'Add Parameter'}</h2>
                <button onClick={() => setParamModalOpen(false)} className="text-gray-500 hover:text-gray-700"><FaTimes style={{ color: 'slategray' }} /></button>
              </div>
              <div className="p-6 pt-2 text-sm text-gray-600 grid gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Parameter Name</label>
                  <input
                    type="text"
                    value={paramForm.display_name}
                    onChange={(e) => setParamForm((prev) => ({ ...prev, display_name: e.target.value }))}
                    disabled={paramEditIndex != null && !isReservedName(parameters[paramEditIndex]?.display_name)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500 ${paramEditIndex != null ? 'bg-gray-50 text-gray-600 cursor-not-allowed' : ''}`}
                    placeholder="Enter parameter name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subparameters Used</label>
                  <div className="flex gap-2">
                    <ModernTimeDropdown
                      value={selectedSubparameter}
                      onChange={(e) => setSelectedSubparameter(e.target.value)}
                      options={availableSubparameterVariables}
                      width="w-full"
                      placeholder="Select subparameter"
                    />
                    <button type="button" onClick={addSubparameterToParameter} className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm">
                      Add
                    </button>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {paramForm.subparameters_used.map((subparam) => (
                      <span key={subparam} className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 px-2 py-1 rounded">
                        {subparam}
                        <button type="button" className="text-gray-500 hover:text-gray-700" onClick={() => removeSubparameterFromParameter(subparam)}>
                          <FaTimes style={{ fontSize: '10px' }} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Output Type</label>
                  <ModernTimeDropdown
                    value={paramForm.output_type === 'Date and Time' ? 'Date and Time (ISO Format - YYYY-MM-DDTHH:MM:SS)' : paramForm.output_type}
                    onChange={(e) => {
                      const raw = e?.target?.value;
                      const normalized = (typeof raw === 'string' && raw.startsWith('Date and Time')) ? 'Date and Time' : raw;
                      setParamForm((prev) => ({ ...prev, output_type: normalized }));
                    }}
                    options={ANALYTICS_TYPES}
                    width="w-full"
                  />
                </div>

                {['Categorical - Single Value', 'Categorical - Multiple Values'].includes(paramForm.output_type) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="analytics-parameter-values">Possible Values</label>
                    <textarea id="analytics-parameter-values" rows={3} value={getArray(paramForm.possible_values).join('\n')} onChange={(e) => setParamForm((prev) => ({ ...prev, possible_values: e.target.value.split('\n') }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="One value per line" />
                    <p className="mt-1 text-xs text-gray-500">Enter one allowed result per line. Scheduling mode values are now, delay, and callback_time.</p>
                  </div>
                )}

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">Python Logic</label>
                    <button type="button" onClick={verifyParameterLogic} className="px-3 py-1 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-xs">
                      {logicLoading ? 'Validating...' : 'Validate Logic'}
                    </button>
                  </div>
                  <textarea
                    value={paramForm.logic}
                    onChange={(e) => setParamForm((prev) => ({ ...prev, logic: e.target.value }))}
                    rows={8}
                    className={`w-full px-3 py-2 border rounded-lg focus:border-indigo-500 ${queryError ? 'border-red-400' : 'border-gray-300'}`}
                    placeholder="Write python-style logic using selected subparameters"
                  />
                  {queryError && <p className="mt-2 text-sm text-red-600">{queryError}</p>}
                </div>
              </div>
              <div className="flex justify-end gap-3 p-6">
                <button onClick={() => setParamModalOpen(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm">
                  Cancel
                </button>
                <button onClick={saveParameter} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm">
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InbuiltAnalyticsConfig;
